import { createContext, type ReactNode, useContext } from "react";

/**
 * Tells a full-screen overlay whether the mobile native back button has taken over
 * closing it.
 *
 * An overlay that covers the whole screen in the WebView is dismissed by native back
 * once something calls {@link useMobileBackHandler} for it. A surface that renders its
 * own close control reads this and suppresses that control when true, so back is the
 * single close affordance rather than a redundant second one. It is an *additional*
 * reason to hide a close button, not a replacement for a surface's existing
 * `hideCloseButton`-style prop.
 *
 * Providers must feed this the **same expression** handed to `useMobileBackHandler`,
 * passed through {@link mobile.canClaimNativeBack}, so the button and the handler
 * cannot disagree about whether back was actually claimed:
 *
 * ```tsx
 * useMobileBackHandler(onClose, isOverlayFullScreen);
 * const isNativeBackReplacingClose = mobile.canClaimNativeBack(isOverlayFullScreen);
 * ```
 *
 * That helper is what keeps the close button visible when nothing can claim back —
 * outside the WebView, or on an older build whose bridge has no
 * `setMobileBackHandlerActive` and so keeps native's default back behavior.
 *
 * `BTSplitView` provides this around its detail pane; a host with its own full-screen
 * overlay outside a split view can provide it directly.
 *
 * `false` outside a provider, so `useIsNativeBackReplacingClose` is safe to call from a
 * component that may render anywhere — a pane reused in a plain drawer or modal keeps
 * its close button untouched.
 */
const NativeBackCloseContext = createContext<boolean>(false);

interface INativeBackCloseProviderProps {
  /** True when native back has claimed this surface's close action. */
  isNativeBackReplacingClose: boolean;
  children: ReactNode;
}

export const NativeBackCloseProvider = ({
  isNativeBackReplacingClose,
  children,
}: INativeBackCloseProviderProps) => (
  <NativeBackCloseContext.Provider value={isNativeBackReplacingClose}>
    {children}
  </NativeBackCloseContext.Provider>
);

/** True when the mobile native back button has taken over closing this surface. */
export const useIsNativeBackReplacingClose = (): boolean =>
  useContext(NativeBackCloseContext);
